import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import { queryKeys } from './queryKeys';
import type { PersonalPatch, PersonalRow, PersonalWrite, UUID } from './types';

export function usePersonal(planId: UUID | null) {
  return useQuery({
    queryKey: planId ? queryKeys.personal.plan(planId) : ['personal', 'none'],
    queryFn: async (): Promise<PersonalRow[]> => {
      if (!planId) return [];
      const res = await apiClient.get<PersonalRow[]>(`/plan/${planId}/personal`);
      return res.data;
    },
    enabled: !!planId,
  });
}

export function useCreatePersonal(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: PersonalWrite): Promise<PersonalRow> => {
      const res = await apiClient.post<PersonalRow>(`/plan/${planId}/personal`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.personal.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useUpdatePersonal(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: UUID; patch: PersonalPatch }): Promise<PersonalRow> => {
      const res = await apiClient.patch<PersonalRow>(
        `/plan/${planId}/personal/${vars.id}`,
        vars.patch,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.personal.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useDeletePersonal(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`/plan/${planId}/personal/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.personal.plan(planId) });
      void qc.invalidateQueries({ queryKey: queryKeys.snapshot.plan(planId) });
    },
  });
}

export function useReorderPersonal(planId: UUID) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: UUID[]): Promise<PersonalRow[]> => {
      const res = await apiClient.post<PersonalRow[]>(
        `/plan/${planId}/personal/reorder`,
        ids,
      );
      return res.data;
    },
    // Optimistic Update: Reihenfolge sofort spiegeln, damit die UI nicht
    // erst auf den Roundtrip warten muss.
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: queryKeys.personal.plan(planId) });
      const previous = qc.getQueryData<PersonalRow[]>(
        queryKeys.personal.plan(planId),
      );
      if (previous) {
        const byId = new Map(previous.map((p) => [p.id, p]));
        const reordered = ids
          .map((id) => byId.get(id))
          .filter((p): p is PersonalRow => p !== undefined);
        qc.setQueryData(queryKeys.personal.plan(planId), reordered);
      }
      return { previous };
    },
    onError: (_err, _ids, ctx) => {
      const previous = (ctx as { previous?: PersonalRow[] } | undefined)?.previous;
      if (previous) {
        qc.setQueryData(queryKeys.personal.plan(planId), previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.personal.plan(planId) });
    },
  });
}
